import React from 'react';
import { connect } from 'react-redux';
import {
  Checkbox,
  Fade,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Remove,
  PlaylistAdd,
  CheckCircle,
  Videocam,
  Description,
  Photo,
  ChatBubbleOutline,
} from '@mui/icons-material';
import ReactTooltip from 'react-tooltip';
import Anime from 'animejs';
import {
  Animations, IconType, NA, Styling, VAULT_MEDIA_TYPE,
} from '../../../../constants';
import { IAppState } from '../../../../state/reducers';
import { IProps, IStateProps, IDispatchProps } from './interfaces';
import {
  ResultsTable,
  ResultsTableHeaderRow,
  ResultsTableBody,
  ResultsTableBodyRow,
  ResultsTableAssetRow,
  ResultsTableCheckboxCell,
  ResultsTableGameCell,
  ResultsTableHeaderCell,
  ResultsTableCell,
  ResultsTableDownloadCell,
  TeamImage,
} from './styles';
import { ShareIcon, ShareAllIcon } from '../../../Shared';
import { selectSelectedTeams } from '../../../../state/exchange/searchParams';
import {
  isAssetAvailable,
  ISelectableExchangeAssetHit,
  toggleGameExpandedState,
  toggleAllGamesExpandedState,
  toggleGameSelection,
  toggleAssetSelection,
  toggleAllGamesSelection,
  queueAssetDownload,
  queueGameDownload,
  queryGames,
} from '../../../../state/exchange/searchResults';
import { activateAsset } from '../../../../state/exchange/activeAsset';
import { isSoftball } from '../../../../utils/exchange';
import { selectHasDownloadPermission, selectIsUserExchangeVendor } from '../../../../state/user/userOrgRole';
import { Sport } from '../../../../enums';

const animateBounce = (e: MouseEvent | KeyboardEvent) => {
  Anime({
    targets: e.currentTarget,
    ...Animations.ANIME_SHARE_BOUNCE,
  });
};

const getAssetIcon = (assetType: string) => {
  switch (assetType) {
    case IconType.VIDEO:
      return <Videocam />;
    case IconType.DOCUMENT:
    case IconType.EXCHANGE:
      return <Description />;
    case IconType.IMAGE:
      return <Photo />;
    default:
      return null;
  }
};

const getAssetSourceGameNumber = (asset: ISelectableExchangeAssetHit): string => {
  if (asset._source.homeTeamId && asset._source.sourceTeamId === asset._source.homeTeamId) {
    return asset._source.teamGameNumber;
  } if (asset._source.awayTeamId && asset._source.sourceTeamId === asset._source.awayTeamId) {
    return asset._source.visitorTeamGameNumber || NA; // remove OR when documents  have field.
  }

  return NA;
};

const pushToCloudText = 'Send to ThunderCloud';
const sendToConsoleText = 'Add to Console Queue';

class ExchangeSearchResultsTable extends React.Component<IProps> {
  isAssetActive = (gameKey: string, assetId: string) => {
    const {
      activeAsset: {
        gameKey: activeGameKey,
        assetId: activeAssetId,
      },
    } = this.props;

    return gameKey === activeGameKey && assetId === activeAssetId;
  };

  componentDidUpdate(prevProps: IProps) {
    const { teamsLoaded, selectedTeams, onSearch } = this.props;
    // if first load and user has selected teams, run initial search
    if (!prevProps.teamsLoaded && teamsLoaded && selectedTeams.length > 0) {
      onSearch();
    }
    ReactTooltip.rebuild();
  }

  onExpandAllGames(e: MouseEvent | KeyboardEvent) {
    const { onAllGamesExpandedStateToggle } = this.props;

    animateBounce(e);
    setTimeout(onAllGamesExpandedStateToggle, Animations.ANIME_SHARE_BOUNCE.duration);
  }

  getDownloadTextAsset(asset: ISelectableExchangeAssetHit): string {
    const { subscription: { isPushToCloud } } = this.props;
    if (!isPushToCloud || asset._source.type !== VAULT_MEDIA_TYPE.video) {
      return sendToConsoleText;
    }
    return pushToCloudText;
  }

  getDownloadTextGame(gameKey: string): string {
    const { games, subscription: { isPushToCloud } } = this.props;
    const game = games.find(({ key }) => key === gameKey);
    if (!isPushToCloud) {
      return sendToConsoleText;
    }

    // Check if all non video
    let totalNonVideo = 0;
    game.assets.forEach((asset) => {
      if (asset._source.type !== VAULT_MEDIA_TYPE.video) {
        totalNonVideo += 1;
      }
    });

    if (totalNonVideo === game.assets.length) {
      return sendToConsoleText;
    }
    return pushToCloudText;
  }

  getDownloadedCheck(asset: ISelectableExchangeAssetHit): JSX.Element {
    const { subscription: { isPushToCloud } } = this.props;
    if (!(asset.isDownloaded || asset.isPushedToCloud)) {
      return null;
    }
    let tooltipText = '';
    if (isPushToCloud) {
      if (asset._source.type !== VAULT_MEDIA_TYPE.video) {
        if (!asset.isDownloaded) {
          return null;
        }
      } else if (!asset.isPushedToCloud) {
        return null;
      }
      tooltipText = 'Asset Previously Pushed To Cloud';
      if (asset._source.type !== VAULT_MEDIA_TYPE.video) {
        tooltipText = 'Asset Previously Downloaded';
      }
    } else {
      if (!asset.isDownloaded) {
        return null;
      }
      tooltipText = 'Asset Previously Downloaded';
    }

    return (
      <Fade in>
        <span
          data-tip={tooltipText}
          data-for="share-tooltip"
        >
          <CheckCircle style={{ color: 'green' }} />
        </span>
      </Fade>
    );
  }

  render() {
    const {
      games,
      subscription: {
        isPushToCloud,
      },
      isAllGamesSelected,
      isAllGamesExpanded,
      canDownload,
      onGameExpandedStateToggle,
      onGameSelectionToggle,
      onGameDownload,
      onAssetSelectionToggle,
      onAllGamesToggle,
      onAssetClick,
      onAssetDownload,
      sport,
      vendorSport,
    } = this.props;

    return (
      <ResultsTable>
        <ResultsTableHeaderRow>
          <ResultsTableCheckboxCell>
            <Checkbox
              checked={isAllGamesSelected}
              onChange={onAllGamesToggle}
              disabled={games === null || games.length === 0}
              color="secondary"
            />
          </ResultsTableCheckboxCell>
          <ResultsTableGameCell>
            <Fade in={games && games.length > 0}>
              <span
                style={{ marginRight: `${Styling.DEFAULT_SPACING_HALVED}px` }}
                onClick={(e) => this.onExpandAllGames(e as unknown as MouseEvent)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    this.onExpandAllGames(e as unknown as MouseEvent);
                  }
                }}
                data-tip={`${isAllGamesExpanded ? 'Collapse' : 'Expand'} All`}
                data-for="share-tooltip"
                role="button"
                tabIndex={0}
              >
                {isAllGamesExpanded ? <Remove /> : <PlaylistAdd />}
              </span>
            </Fade>
            Game
          </ResultsTableGameCell>
          <ResultsTableHeaderCell>
            File Type
          </ResultsTableHeaderCell>
          <ResultsTableHeaderCell>
            Shooter Game #
          </ResultsTableHeaderCell>
          <ResultsTableHeaderCell>
            Shooter
          </ResultsTableHeaderCell>
          <ResultsTableDownloadCell>
            {isPushToCloud ? 'Pushed To Cloud' : 'Downloaded'}
          </ResultsTableDownloadCell>
          <ResultsTableDownloadCell />
        </ResultsTableHeaderRow>
        <ResultsTableBody>
          {games !== null && games.map(({
            key,
            assets,
            homeTeamImage,
            awayTeamImage,
            isExpanded,
            isAllAssetsSelected,
            isAllAssetsHidden,
          }) => !isAllAssetsHidden && (
            <React.Fragment
              key={key}
            >
              <ResultsTableBodyRow
                data-hometeam={assets[0]._source.homeTeam}
                data-awayteam={assets[0]._source.awayTeam}
                data-game={key}
                data-tip="Game"
                data-gameid={assets[0]._source.gameId}
                onClick={() => onGameExpandedStateToggle(key)}
              >
                <ResultsTableCheckboxCell data-auto={key} data-tip="Checkbox">
                  <Checkbox
                    onClick={(e) => e.stopPropagation()}
                    checked={isAllAssetsSelected}
                    onChange={() => onGameSelectionToggle(key)}
                    color="secondary"
                  />
                </ResultsTableCheckboxCell>
                <ResultsTableGameCell data-tip="Expand Game Parts">
                  {isExpanded ? <Remove /> : <Add />}
                  <TeamImage src={awayTeamImage} />
                  @
                  <TeamImage src={homeTeamImage} />
                  {`${key} (${assets.filter(isAssetAvailable).length})`}
                </ResultsTableGameCell>
                <ResultsTableCell />
                <ResultsTableDownloadCell>
                  {!canDownload && vendorSport === Sport.Football && !isPushToCloud ? (
                    <></>
                  ) : (
                    <span
                      data-tip={this.getDownloadTextGame(key)}
                      data-for="share-tooltip"
                      data-tip-disable={isSoftball(sport)}
                    >
                      <ShareAllIcon
                        disabled={isSoftball(sport)}
                        onClick={(e: MouseEvent) => {
                          e.stopPropagation();
                          onGameDownload(key);
                          animateBounce(e);
                        }}
                      />
                    </span>
                  )}
                </ResultsTableDownloadCell>
              </ResultsTableBodyRow>
              {isExpanded && assets.map((asset: ISelectableExchangeAssetHit) => isAssetAvailable(asset) && (
                <ResultsTableAssetRow
                  data-gameid={asset._source.gameId}
                  data-tip="Game Part"
                  data-qualifier={asset._source.exchangeQualifier}
                  data-hometeam={asset._source.homeTeam}
                  data-awayteam={asset._source.awayTeam}
                  data-shooter-id={asset._source.sourceTeamId}
                  key={asset._id}
                  onClick={() => onAssetClick(key, asset._id)}
                  $isActive={this.isAssetActive(key, asset._id)}
                >
                  <ResultsTableCheckboxCell>
                    <Checkbox
                      onClick={(e) => e.stopPropagation()}
                      checked={asset.isSelected}
                      onChange={() => onAssetSelectionToggle(key, asset._id)}
                      color="secondary"
                    />
                  </ResultsTableCheckboxCell>
                  <ResultsTableGameCell>
                    {asset._source.title}
                    {/*
                      VV-226: an indicator rather than a column - the table is already wide
                      and free text would wrap badly. Anyone who can see the asset can read
                      the comment, so this needs no permission check: OpenSearch already
                      filters hits by securityProfiles.
                    */}
                    {asset._source.exchangeComment && (
                      <Tooltip
                        title={asset._source.exchangeComment}
                        componentsProps={{ tooltip: { sx: { fontSize: '0.85rem', whiteSpace: 'pre-wrap' } } }}
                      >
                        {/*
                          Focusable wrapper, and the comment itself as the accessible label: an
                          SVG cannot take focus, so without this the tooltip is mouse-only and a
                          screen reader would only learn that a comment exists, not what it says.
                          onClick is stopped so opening the tooltip does not also select the row.
                        */}
                        <button
                          type="button"
                          aria-label={`Comment: ${asset._source.exchangeComment}`}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            marginLeft: 6,
                            display: 'inline-flex',
                            verticalAlign: 'middle',
                            padding: 0,
                            border: 'none',
                            background: 'none',
                            color: 'inherit',
                            cursor: 'pointer',
                          }}
                        >
                          <ChatBubbleOutline fontSize="small" aria-hidden />
                        </button>
                      </Tooltip>
                    )}
                  </ResultsTableGameCell>
                  <ResultsTableCell>
                    {getAssetIcon(asset._source.type)}
                  </ResultsTableCell>
                  <ResultsTableCell>
                    <span>{getAssetSourceGameNumber(asset)}</span>
                  </ResultsTableCell>
                  <ResultsTableCell>
                    <TeamImage src={asset.sourceTeamImage} />
                  </ResultsTableCell>
                  <ResultsTableDownloadCell>
                    {this.getDownloadedCheck(asset)}
                  </ResultsTableDownloadCell>
                  <ResultsTableDownloadCell>
                    {!canDownload && vendorSport === Sport.Football && !isPushToCloud ? (
                      <></>
                    ) : (
                      <span
                        data-tip={this.getDownloadTextAsset(asset)}
                        data-for="share-tooltip"
                        data-tip-disabled={isSoftball(sport)}
                      >
                        <ShareIcon
                          disabled={isSoftball(sport)}
                          onClick={(e: MouseEvent) => {
                            e.stopPropagation();
                            onAssetDownload(key, asset._id);
                            animateBounce(e);
                          }}
                        />
                      </span>
                    )}
                  </ResultsTableDownloadCell>
                </ResultsTableAssetRow>
              ))}
            </React.Fragment>
          ))}
        </ResultsTableBody>
      </ResultsTable>
    );
  }
}

const mapStateToProps = (state: IAppState): IStateProps => ({
  games: state.exchange.searchResults.games,
  subscription: state.exchange.contentPreferences.subscription,
  isAllGamesSelected: state.exchange.searchResults.isAllGamesSelected,
  isAllGamesExpanded: state.exchange.searchResults.isAllGamesExpanded,
  activeAsset: state.exchange.activeAsset,
  teamsLoaded: state.exchange.searchParams.isLoaded,
  selectedTeams: selectSelectedTeams(state),
  sport: state.exchange.initExchange.sport,
  canDownload: selectHasDownloadPermission(state),
  vendorSport: state.exchange.searchParams.sportFilter,
});

const mapDispatchToProps: IDispatchProps = {
  onGameSelectionToggle: toggleGameSelection,
  onGameExpandedStateToggle: toggleGameExpandedState,
  onAllGamesExpandedStateToggle: toggleAllGamesExpandedState,
  onGameDownload: queueGameDownload,
  onAssetSelectionToggle: toggleAssetSelection,
  onAllGamesToggle: toggleAllGamesSelection,
  onAssetClick: activateAsset,
  onAssetDownload: queueAssetDownload,
  onSearch: queryGames,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ExchangeSearchResultsTable);
